import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  ChefHat,
  Clock,
  CreditCard,
  Package,
  Printer,
  RefreshCw,
  Search,
  Truck,
  X,
} from 'lucide-react';
import Modal from '../../components/ui/Modal';
import type { Order, OrderStatus, PaymentMethod, PrintActionResponse, PrintJob, PrinterConfig, TableCommandSummary } from '../../types';
import { formatPrice, timeAgo } from '../../lib/mockData';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { normalizeOrder, toBackendOrderStatus } from '../../lib/backendAdapters';
import { detectDevicePlatform, executePrintLaunchAction, updatePrintJobStatus } from '../../lib/printing';
import { persistence } from '../../lib/persistence';
import { WifiOff, ShieldCheck } from 'lucide-react';


const STATUS_COLUMNS: { status: OrderStatus; label: string; icon: React.ElementType; color: string }[] = [
  { status: 'pending', label: 'Aguardando', icon: Clock, color: 'text-warning' },
  { status: 'confirmed', label: 'Confirmados', icon: CheckCircle, color: 'text-primary' },
  { status: 'preparing', label: 'Preparando', icon: ChefHat, color: 'text-primary' },
  { status: 'ready', label: 'Pronto', icon: CheckCircle, color: 'text-success' },
  { status: 'delivered', label: 'Entregue', icon: Package, color: 'text-muted-foreground' },
  { status: 'cancelled', label: 'Cancelados', icon: X, color: 'text-destructive' },
];

const NEXT_STATUS: Partial<Record<OrderStatus, OrderStatus>> = {
  confirmed: 'preparing',
  pending: 'preparing',
  preparing: 'ready',
  ready: 'delivered',
};

const paymentMethodLabel: Record<PaymentMethod, string> = {
  pix: 'Pix',
  mercado_pago: 'Mercado Pago',
  card_on_delivery: 'Cartao na entrega',
  cash: 'Dinheiro',
};

const TERMINAL_STATUSES: OrderStatus[] = ['delivered', 'cancelled'];

const supportsBrowserNotifications = () =>
  typeof window !== 'undefined' && 'Notification' in window;

const pushBrowserNotification = (title: string, body: string) => {
  if (!supportsBrowserNotifications() || Notification.permission !== 'granted') {
    return;
  }

  try {
    new Notification(title, { body });
  } catch (error) {
    console.warn('Nao foi possivel enviar notificacao do navegador.', error);
  }
};

const isOrderReadyForAutomaticPrinting = (
  order: Order,
  wasSeenBefore: boolean,
  previousStatus?: OrderStatus,
) => {
  if (TERMINAL_STATUSES.includes(order.status) || order.status === 'awaiting_payment') {
    return false;
  }

  if (order.paymentMethod === 'mercado_pago') {
    if (order.paymentStatus !== 'paid' || order.status !== 'confirmed') {
      return false;
    }

    return !wasSeenBefore || (!!previousStatus && previousStatus !== 'confirmed');
  }

  return !wasSeenBefore;
};

const describeOrdersForNotification = (items: Order[]) => {
  if (items.length === 1) {
    const [order] = items;
    const location = order.type === 'table'
      ? `Mesa ${order.tableNumber}`
      : order.type === 'delivery'
        ? 'Delivery'
        : 'Retirada';
    return `Novo pedido de ${location} aguardando impressao.`;
  }

  return `${items.length} novos pedidos aguardando impressao.`;
};

const sortOrdersForDisplay = (items: Order[], status: OrderStatus) => {
  const sorted = [...items];
  sorted.sort((a, b) => {
    const first = new Date(a.createdAt).getTime();
    const second = new Date(b.createdAt).getTime();
    return TERMINAL_STATUSES.includes(status) ? second - first : first - second;
  });
  return sorted;
};

const orderTypeLabel: Record<Order['type'], string> = {
  table: 'Mesa',
  delivery: 'Delivery',
  counter: 'Retirada',
};

const OrderCard: React.FC<{
  order: Order;
  onAdvance: (id: string, currentStatus: OrderStatus) => void;
  onCancel: (id: string) => void;
  onPrint: (order: Order) => void;
  queuePosition?: number;
  showPrintButton: boolean;
  printJob?: PrintJob;
  actionLoading?: 'advance' | 'cancel' | null;
}> = ({ order, onAdvance, onCancel, onPrint, queuePosition, showPrintButton, printJob, actionLoading = null }) => {
  const typeLabel =
    order.type === 'table'
      ? `Mesa ${order.tableNumber}`
      : order.type === 'delivery'
        ? 'Delivery'
        : 'Retirada';
  const TypeIcon = order.type === 'delivery' ? Truck : Package;
  const canAdvance = !!NEXT_STATUS[order.status];
  const paymentLabel = order.paymentMethod ? paymentMethodLabel[order.paymentMethod] : null;
  const compactSummary = order.items?.length
    ? `${order.items.reduce((sum, item: any) => sum + (item.quantity || 0), 0)} item(ns) neste lancamento`
    : 'Sem itens detalhados';

  return (
    <div className="card animate-slide-in-up space-y-3 p-4 transition-colors hover:border-white/10">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-[11px] text-muted-foreground">{order.id.slice(-6).toUpperCase()}</p>
          {typeof queuePosition === 'number' && !TERMINAL_STATUSES.includes(order.status) && (
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-yellow-300">
              {queuePosition + 1}o da fila
            </p>
          )}
          <p className="mt-0.5 text-sm font-bold text-white">{order.customerName}</p>
          {order.customerPhone && (
            <p className="mt-1 text-xs text-muted-foreground">{order.customerPhone}</p>
          )}
          {order.type === 'delivery' && order.deliveryAddress && (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{order.deliveryAddress}</p>
          )}
        </div>
        <span className="flex items-center gap-1.5 rounded-full bg-[rgba(26,26,26,1)] px-2.5 py-1 text-xs font-semibold text-muted-foreground">
          <TypeIcon className="h-3.5 w-3.5" />
          {typeLabel}
        </span>
      </div>

      {(paymentLabel || order.paymentStatus) && (
        <div className="flex flex-wrap gap-2">
          {paymentLabel && (
            <span className="rounded-full bg-white/[0.05] px-2.5 py-1 text-[11px] text-white/70">
              {paymentLabel}
            </span>
          )}
          {order.paymentStatus && (
            <span className={`rounded-full px-2.5 py-1 text-[11px] ${
              order.paymentStatus === 'paid'
                ? 'bg-emerald-500/12 text-emerald-300'
                : order.paymentStatus === 'pending'
                  ? 'bg-yellow-500/12 text-yellow-300'
                  : 'bg-red-500/12 text-red-300'
            }`}>
              {order.paymentStatus === 'paid'
                ? 'Pago'
                : order.paymentStatus === 'pending'
                  ? 'Pagamento pendente'
                  : order.paymentStatus === 'expired'
                    ? 'Expirado'
                    : 'Falhou'}
            </span>
          )}
          {order.paymentMethod === 'cash' && order.cashNeedsChange && order.cashChangeFor && (
            <span className="rounded-full bg-white/[0.05] px-2.5 py-1 text-[11px] text-white/70">
              Troco para {formatPrice(order.cashChangeFor)}
            </span>
          )}
          {printJob && (
            <span className={`rounded-full px-2.5 py-1 text-[11px] ${
              printJob.status === 'SUCCEEDED'
                ? 'bg-emerald-500/12 text-emerald-300'
                : printJob.status === 'FAILED'
                  ? 'bg-red-500/12 text-red-300'
                  : 'bg-blue-500/12 text-blue-300'
            }`}>
              Impressao {printJob.status.toLowerCase()}
            </span>
          )}
        </div>
      )}

      {order.type === 'table' ? (
        <div className="rounded-2xl border border-white/8 bg-[#111] px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary/80">
            Pedido adicionado a comanda
          </p>
          <p className="mt-1 text-sm text-white/80">{compactSummary}</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {order.items?.map((item: any) => (
            <div key={item.id} className="flex justify-between gap-3 text-sm">
              <div className="min-w-0">
                <span className="text-white/80">
                  {item.quantity}x {item.product?.name || item.productName}
                </span>
                {item.notes && (
                  <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                    {item.notes}
                  </p>
                )}
              </div>
              <span className="text-muted-foreground">{formatPrice(item.subtotal)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="divider flex items-center justify-between pt-2">
        <div>
          <p className="text-[11px] font-medium text-yellow-300">{timeAgo(order.createdAt)}</p>
          <p className="text-sm font-bold text-white">{formatPrice(order.total)}</p>
        </div>
        <div className="flex gap-2">
          {showPrintButton && (
            <button
              onClick={() => onPrint(order)}
              className="rounded-lg border border-[#2A2A2A] p-2 text-muted-foreground transition-all hover:border-primary/40 hover:text-primary"
              title="Imprimir"
            >
              <Printer className="h-4 w-4" />
            </button>
          )}
          {order.status !== 'delivered' && order.status !== 'cancelled' && (
            <button
              onClick={() => onCancel(order.id)}
              disabled={!!actionLoading}
              className="rounded-lg p-2 text-muted-foreground transition-all hover:bg-destructive/20 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-60"
              title="Cancelar"
            >
              {actionLoading === 'cancel' ? <span className="text-[11px] font-semibold">...</span> : <X className="h-4 w-4" />}
            </button>
          )}
          {canAdvance && (
            <button
              onClick={() => onAdvance(order.id, order.status)}
              disabled={!!actionLoading}
              className="btn-primary flex items-center gap-1.5 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {actionLoading === 'advance'
                ? 'Atualizando...'
                : NEXT_STATUS[order.status] === 'preparing'
                  ? 'Confirmar'
                  : NEXT_STATUS[order.status] === 'ready'
                    ? 'Pronto'
                    : 'Entregar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AwaitingOnlinePaymentCard: React.FC<{ order: Order; onCancel: (id: string) => void; cancelling?: boolean }> = ({ order, onCancel, cancelling = false }) => (
  <div className="card space-y-4 border-yellow-500/20 bg-yellow-500/[0.03] p-4">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-mono text-[11px] text-muted-foreground">{order.id.slice(-6).toUpperCase()}</p>
        <p className="mt-1 text-sm font-bold text-white">{order.customerName}</p>
        <p className="mt-1 text-xs text-muted-foreground">{order.customerPhone}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{order.deliveryAddress}</p>
      </div>
      <span className="flex items-center gap-1.5 rounded-full bg-yellow-500/12 px-2.5 py-1 text-xs font-semibold text-yellow-300">
        <CreditCard className="h-3.5 w-3.5" />
        Checkout pendente
      </span>
    </div>
    <div className="rounded-2xl border border-white/8 bg-[#111] p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-white/75">Total</span>
        <span className="text-sm font-bold text-white">{formatPrice(order.total)}</span>
      </div>
      <p className="mt-2 text-xs font-medium text-yellow-300">
        Checkout gerado {timeAgo(order.createdAt)}
      </p>
    </div>
    <div className="rounded-2xl border border-white/8 bg-[#111] px-3 py-2 text-xs text-muted-foreground">
      Aguardando retorno do Mercado Pago para liberar o preparo.
    </div>
    <div className="flex justify-end">
      <button
        onClick={() => onCancel(order.id)}
        disabled={cancelling}
        className="rounded-xl border border-red-500/20 px-3 py-2 text-sm font-semibold text-red-300 transition hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {cancelling ? 'Cancelando...' : 'Cancelar delivery'}
      </button>
    </div>
  </div>
);

const AdminOrders: React.FC = () => {
  const { user } = useAuthStore();
  const [orders, setOrders] = useState<Order[]>([]);
  const [tableCommands, setTableCommands] = useState<TableCommandSummary[]>([]);
  const [printConfigs, setPrintConfigs] = useState<PrinterConfig[]>([]);
  const [printJobs, setPrintJobs] = useState<PrintJob[]>([]);
  const [printingModuleEnabled, setPrintingModuleEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [printFeedback, setPrintFeedback] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastSync, setLastSync] = useState<string | null>(persistence.getLastSync());

  const [closingCommandTable, setClosingCommandTable] = useState<string | null>(null);
  const [updatingOrderActionKey, setUpdatingOrderActionKey] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<TableCommandSummary | null>(null);
  const [closeCommandPaymentMethod, setCloseCommandPaymentMethod] = useState<PaymentMethod>('cash');
  const [closeCommandMarkAsPaid, setCloseCommandMarkAsPaid] = useState(false);
  const [selectedPrinterConfigId, setSelectedPrinterConfigId] = useState('');
  const [printModalMode, setPrintModalMode] = useState<'manual' | 'prompt'>('manual');
  const [orderTypeFilter, setOrderTypeFilter] = useState<'all' | Order['type']>('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [boardView, setBoardView] = useState<'operation' | 'commands'>('operation');
  const currentPlatform = useMemo(() => detectDevicePlatform(), []);
  const seenOrderIdsRef = useRef<Set<string>>(new Set());
  const autoPromptedOrderIdsRef = useRef<Set<string>>(new Set());
  const orderStatusSnapshotRef = useRef<Map<string, OrderStatus>>(new Map());
  const queuedAutomaticPrintOrderIdsRef = useRef<Set<string>>(new Set());
  const automaticPrintQueueRef = useRef<Array<{ order: Order; printerConfigId: string }>>([]);
  const automaticPrintInFlightRef = useRef(false);
  const loadOrdersRef = useRef<(() => Promise<void>) | null>(null);
  const requestPrintRef = useRef<
    ((
      order: Order,
      printerConfigId?: string,
      triggerSource?: 'MANUAL' | 'AUTO_PROMPT',
      options?: { suppressReload?: boolean; suppressModalClose?: boolean },
    ) => Promise<void>) | null
  >(null);

  const activePrintConfigs = useMemo(
    () => printConfigs.filter((config) => config.isActive),
    [printConfigs],
  );

  const promptConfigs = useMemo(
    () => activePrintConfigs.filter((config) => config.printOnNewOrder),
    [activePrintConfigs],
  );

  const latestPrintJobByOrder = useMemo(() => {
    const result = new Map<string, PrintJob>();
    const sortedJobs = [...printJobs].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    sortedJobs.forEach((job) => {
      if (job.orderId && !result.has(job.orderId)) {
        result.set(job.orderId, job);
      }
    });

    return result;
  }, [printJobs]);

  const printablePendingOrdersCount = useMemo(
    () =>
      orders.filter((order) => {
        if (TERMINAL_STATUSES.includes(order.status) || order.status === 'awaiting_payment') {
          return false;
        }

        if (order.paymentMethod === 'mercado_pago') {
          return order.paymentStatus !== 'paid';
        }

        return printingModuleEnabled && activePrintConfigs.length === 0;
      }).length,
    [activePrintConfigs.length, orders, printingModuleEnabled],
  );

  const filteredTableCommands = useMemo(() => {
    const query = orderSearch.trim().toLowerCase();

    return tableCommands.filter((command) => {
      if (orderTypeFilter !== 'all' && orderTypeFilter !== 'table') {
        return false;
      }

      if (!query) {
        return true;
      }

      const searchableText = [
        command.tableNumber,
        ...command.customerNames,
        ...command.items.map((item) => item.name),
      ]
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [orderSearch, orderTypeFilter, tableCommands]);

  const filteredOrders = useMemo(() => {
    const query = orderSearch.trim().toLowerCase();

    return orders.filter((order) => {
      if (orderTypeFilter !== 'all' && order.type !== orderTypeFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      const searchableText = [
        order.id,
        order.customerName,
        order.customerPhone,
        order.tableNumber,
        order.deliveryAddress,
        ...(order.items ?? []).map((item: any) => item.product?.name || item.productName || ''),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [orderSearch, orderTypeFilter, orders]);

  const drainAutomaticPrintQueue = useCallback(async () => {
    if (automaticPrintInFlightRef.current) {
      return;
    }

    automaticPrintInFlightRef.current = true;

    try {
      while (automaticPrintQueueRef.current.length > 0) {
        const nextEntry = automaticPrintQueueRef.current.shift();
        if (!nextEntry) {
          continue;
        }

        queuedAutomaticPrintOrderIdsRef.current.delete(nextEntry.order.id);
        await requestPrintRef.current?.(
          nextEntry.order,
          nextEntry.printerConfigId,
          'AUTO_PROMPT',
          { suppressReload: true, suppressModalClose: true },
        );
      }

      await loadOrdersRef.current?.();
    } finally {
      automaticPrintInFlightRef.current = false;
    }
  }, []);

  const enqueueAutomaticPrints = useCallback((items: Order[], printerConfigId: string) => {
    items.forEach((order) => {
      if (queuedAutomaticPrintOrderIdsRef.current.has(order.id)) {
        return;
      }

      queuedAutomaticPrintOrderIdsRef.current.add(order.id);
      automaticPrintQueueRef.current.push({ order, printerConfigId });
    });

    if (automaticPrintQueueRef.current.length > 0) {
      void drainAutomaticPrintQueue();
    }
  }, [drainAutomaticPrintQueue]);

  const loadOrders = useCallback(async () => {
    if (!user?.establishmentId) {
      setLoadError('Nao foi possivel identificar o restaurante da sua conta.');
      setIsLoading(false);
      return;
    }

    try {
      setLoadError('');
      const [ordersResponse, tableCommandsResponse, configsResponse, jobsResponse, establishmentResponse] = await Promise.all([
        api.get(`/orders/establishment/${user.establishmentId}`),
        api.get(`/orders/establishment/${user.establishmentId}/table-commands/open`),
        api.get('/printer-configs'),
        api.get('/print-jobs'),
        api.get(`/establishments/id/${user.establishmentId}`),
      ]);

      const nextOrders = (ordersResponse.data ?? []).map(normalizeOrder);
      const nextTableCommands = tableCommandsResponse.data ?? [];
      const nextConfigs = configsResponse.data ?? [];
      const nextJobs = jobsResponse.data ?? [];
      const nextModuleEnabled = establishmentResponse.data?.settings?.printingModuleEnabled !== false;
      const previousSeenOrderIds = new Set(seenOrderIdsRef.current);
      const previousOrderStatusSnapshot = new Map(orderStatusSnapshotRef.current);

      setOrders(nextOrders);
      setTableCommands(nextTableCommands);
      setPrintConfigs(nextConfigs);
      setPrintJobs(nextJobs);
      setPrintingModuleEnabled(nextModuleEnabled);

      const nextIds = new Set<string>(nextOrders.map((order: Order) => order.id));
      const isFirstLoad = previousSeenOrderIds.size === 0;

      if (!isFirstLoad && nextModuleEnabled) {
        const nextPromptConfigs = nextConfigs.filter((config: PrinterConfig) => config.isActive && config.printOnNewOrder);
        const autoPrintConfigs = nextPromptConfigs.length > 0 ? nextPromptConfigs : nextConfigs.filter((config: PrinterConfig) => config.isActive);
        const nextAutoPromptEligibleConfigs = autoPrintConfigs.filter(
          (config: PrinterConfig) => config.providerMetadata.supportsAutoPrompt,
        );
        const ordersReadyToPrint = nextOrders
          .filter((order: Order) => {
            const wasSeenBefore = previousSeenOrderIds.has(order.id);
            const previousStatus = previousOrderStatusSnapshot.get(order.id);

            if (autoPromptedOrderIdsRef.current.has(order.id)) {
              return false;
            }

            return isOrderReadyForAutomaticPrinting(order, wasSeenBefore, previousStatus);
          })
          .sort(
            (first: Order, second: Order) =>
              new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime(),
          );

        if (ordersReadyToPrint.length > 0 && autoPrintConfigs.length === 0) {
          ordersReadyToPrint.forEach((order: Order) => autoPromptedOrderIdsRef.current.add(order.id));
          const notificationMessage = describeOrdersForNotification(ordersReadyToPrint);
          setPrintFeedback(`Nenhuma impressora ativa cadastrada. ${notificationMessage}`);
          pushBrowserNotification('Impressao pendente no Garcom Digital', notificationMessage);
        } else if (ordersReadyToPrint.length > 0) {
          ordersReadyToPrint.forEach((order: Order) => autoPromptedOrderIdsRef.current.add(order.id));

          if (
            nextAutoPromptEligibleConfigs.length === 1
            && (
              currentPlatform !== 'DESKTOP'
              || nextAutoPromptEligibleConfigs[0].provider === 'GARCOM_DIGITAL_BRIDGE'
            )
          ) {
            enqueueAutomaticPrints(ordersReadyToPrint, nextAutoPromptEligibleConfigs[0].id);
          } else {
            const [firstOrder] = ordersReadyToPrint;
            const defaultConfig = autoPrintConfigs[0];

            setSelectedOrder(firstOrder);
            setSelectedPrinterConfigId(defaultConfig?.id || '');
            setPrintModalMode('prompt');

            if (ordersReadyToPrint.length > 1) {
              setPrintFeedback(`${ordersReadyToPrint.length} novos pedidos aguardam impressao. Comece pelo primeiro da fila.`);
            }
          }
        }
      }

      seenOrderIdsRef.current = nextIds;
      orderStatusSnapshotRef.current = new Map(
        nextOrders.map((order: Order) => [order.id, order.status]),
      );

      // Persistir dados para visualizacao offline
      persistence.save('ORDERS', nextOrders);
      persistence.save('TABLE_COMMANDS', nextTableCommands);
      persistence.save('PRINTER_CONFIGS', nextConfigs);
      setLastSync(new Date().toISOString());
      setIsOffline(false);

    } catch (error: any) {
      console.error('Failed to load orders', error);
      
      setIsOffline(true);
      setLoadError(
        error?.response?.data?.message
        || 'Nao foi possivel carregar os pedidos. Exibindo dados salvos localmente.',

      );

    } finally {
      setIsLoading(false);
    }

  }, [currentPlatform, enqueueAutomaticPrints, user?.establishmentId]);

  loadOrdersRef.current = loadOrders;

  useEffect(() => {
    // Carregar dados iniciais do cache para segurança (offline mode)
    const cachedOrders = persistence.load<Order[]>('ORDERS');
    const cachedCommands = persistence.load<TableCommandSummary[]>('TABLE_COMMANDS');
    const cachedConfigs = persistence.load<PrinterConfig[]>('PRINTER_CONFIGS');

    if (cachedOrders) setOrders(cachedOrders);
    if (cachedCommands) setTableCommands(cachedCommands);
    if (cachedConfigs) setPrintConfigs(cachedConfigs);

    void loadOrders();

    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const interval = setInterval(() => void loadOrders(), 30000);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadOrders]);



  const advance = async (id: string, currentStatus: OrderStatus) => {
    const nextStatus = NEXT_STATUS[currentStatus];
    if (!nextStatus) return;

    setUpdatingOrderActionKey(`${id}:advance`);
    setOrders((prev) =>
      prev.map((order) =>
        order.id === id ? { ...order, status: nextStatus, updatedAt: new Date().toISOString() } : order,
      ),
    );

    try {
      await api.patch(`/orders/${id}/status`, { status: toBackendOrderStatus(nextStatus) });
    } catch (error) {
      console.error('Failed to update order status', error);
      void loadOrders();
    } finally {
      setUpdatingOrderActionKey(null);
    }
  };

  const cancel = async (id: string) => {
    setUpdatingOrderActionKey(`${id}:cancel`);
    setOrders((prev) =>
      prev.map((order) =>
        order.id === id ? { ...order, status: 'cancelled', updatedAt: new Date().toISOString() } : order,
      ),
    );

    try {
      await api.patch(`/orders/${id}/status`, { status: toBackendOrderStatus('cancelled') });
    } catch (error) {
      console.error('Failed to cancel order', error);
      void loadOrders();
    } finally {
      setUpdatingOrderActionKey(null);
    }
  };

  const closeTableCommand = async (command: TableCommandSummary) => {
    const normalizedTable = command.tableNumber.trim();
    if (!normalizedTable) return;

    setClosingCommandTable(normalizedTable);

    try {
      await api.post(`/orders/table-commands/${command.id}/close`, {
        markAsPaid: closeCommandMarkAsPaid,
        paymentMethod: closeCommandMarkAsPaid ? String(closeCommandPaymentMethod).toUpperCase() : undefined,
      });
      setPrintFeedback(
        closeCommandMarkAsPaid
          ? `Comanda da mesa ${normalizedTable} fechada e marcada como paga.`
          : `Comanda da mesa ${normalizedTable} fechada sem baixa automatica de pagamento.`,
      );
      await loadOrders();
    } catch (error) {
      console.error('Failed to close table command', error);
      setPrintFeedback(`Nao foi possivel fechar a comanda da mesa ${normalizedTable}.`);
      await loadOrders();
    } finally {
      setClosingCommandTable(null);
    }
  };

  const requestCloseCommand = (command: TableCommandSummary) => {
    setSelectedCommand(command);
    setCloseCommandPaymentMethod('cash');
    setCloseCommandMarkAsPaid(false);
  };

  const focusTableOrders = (tableNumber: string) => {
    setBoardView('operation');
    setOrderTypeFilter('table');
    setOrderSearch(tableNumber);
  };

  const openPrintModal = (order: Order, mode: 'manual' | 'prompt') => {
    const defaultConfig = mode === 'prompt'
      ? promptConfigs[0] || activePrintConfigs[0]
      : activePrintConfigs[0];

    setSelectedOrder(order);
    setSelectedPrinterConfigId(defaultConfig?.id || '');
    setPrintModalMode(mode);
  };

  const closePrintModal = () => {
    setSelectedOrder(null);
    setSelectedPrinterConfigId('');
    setPrintModalMode('manual');
  };

  const runPrintAction = async (action: PrintActionResponse) => {
    if (action.launchActionType === 'none') {
      setPrintFeedback(action.userMessage);
      return;
    }

    await executePrintLaunchAction(action);
    await updatePrintJobStatus(action.jobId, 'LAUNCHED', currentPlatform);

    const succeeded = window.confirm(
      `${action.userMessage}\n\nO operador confirmou que a impressao foi enviada corretamente?`,
    );

    await updatePrintJobStatus(
      action.jobId,
      succeeded ? 'SUCCEEDED' : 'FAILED',
      currentPlatform,
      succeeded ? undefined : 'Fluxo iniciado, mas o operador nao confirmou o resultado.',
    );

    setPrintFeedback(
      succeeded
        ? 'Impressao confirmada pelo operador.'
        : 'Tentativa registrada, mas sem confirmacao de impressao.',
    );
  };

  const printSelectedOrder = async () => {
    if (!selectedOrder) return;
    await requestPrint(selectedOrder, selectedPrinterConfigId || undefined, printModalMode === 'prompt' ? 'AUTO_PROMPT' : 'MANUAL');
  };

  const requestPrint = async (
    order: Order,
    printerConfigId?: string,
    triggerSource: 'MANUAL' | 'AUTO_PROMPT' = 'MANUAL',
    options?: { suppressReload?: boolean; suppressModalClose?: boolean },
  ) => {
    if (!order) return;

    try {
      setIsPrinting(true);
      const { data } = await api.post(`/orders/${order.id}/print`, {
        printerConfigId,
        devicePlatform: currentPlatform,
        triggerSource,
      });
      await runPrintAction(data);
      if (!options?.suppressModalClose) {
        closePrintModal();
      }
      if (!options?.suppressReload) {
        await loadOrders();
      }
    } catch (error: any) {
      console.error('Failed to print order', error);
      const responseData = error?.response?.data;
      if (responseData?.jobId) {
        await updatePrintJobStatus(
          responseData.jobId,
          'FAILED',
          currentPlatform,
          responseData?.message || 'Falha ao iniciar a impressao.',
        );
      }
      setPrintFeedback(responseData?.message || error?.message || 'Nao foi possivel imprimir este pedido.');
    } finally {
      setIsPrinting(false);
    }
  };

  requestPrintRef.current = requestPrint;


  const awaitingOnlinePaymentOrders = orders.filter(
    (order) => order.type === 'delivery' && order.paymentMethod === 'mercado_pago' && order.status === 'awaiting_payment',
  );
  const sortedAwaitingOnlinePaymentOrders = sortOrdersForDisplay(awaitingOnlinePaymentOrders, 'awaiting_payment');

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Pedidos</h1>
            <p className="mt-1 text-sm text-muted-foreground">Board em tempo real</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {isOffline ? (
              <div className="flex flex-col items-end">
                <span className="badge flex items-center gap-1.5 border-amber-500/50 bg-amber-500/10 text-amber-500">
                  <WifiOff className="h-3.5 w-3.5" />
                  Modo Offline de Segurança
                </span>
                {lastSync && (
                  <span className="mt-1 text-[10px] text-amber-200/50">
                    Última sincronização: {new Date(lastSync).toLocaleTimeString('pt-BR')}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-end">
                <span className="badge flex items-center gap-1.5 border-emerald-500/50 bg-emerald-500/10 text-emerald-400">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Conectado & Sincronizado
                </span>
                {lastSync && (
                  <span className="mt-1 text-[10px] text-emerald-400/40">
                    Dados salvos localmente
                  </span>
                )}
              </div>
            )}
            <span className="badge badge-warning">{awaitingOnlinePaymentOrders.length} aguardando checkout</span>
            <span className="badge badge-warning">{orders.filter((order) => order.status === 'pending').length} novos</span>
            <span className="badge badge-primary">{orders.filter((order) => order.status === 'preparing').length} em preparo</span>

            {printingModuleEnabled && activePrintConfigs.length > 0 && (
              <span className="badge badge-primary">{activePrintConfigs.length} estacoes de impressao</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
          <div className="rounded-2xl border border-primary/20 bg-primary/[0.05] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">Comandas</p>
            <p className="mt-2 text-2xl font-black text-white">{tableCommands.length}</p>
            <p className="mt-1 text-sm text-muted-foreground">mesas com consumo em aberto</p>
          </div>
          <div className="rounded-2xl border border-white/8 bg-[#151515] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/60">Pedidos de mesa</p>
            <p className="mt-2 text-2xl font-black text-white">{orders.filter((order) => order.type === 'table' && !TERMINAL_STATUSES.includes(order.status)).length}</p>
            <p className="mt-1 text-sm text-muted-foreground">lancamentos ativos no salao</p>
          </div>
          <div className="rounded-2xl border border-white/8 bg-[#151515] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/60">Ticket em aberto</p>
            <p className="mt-2 text-2xl font-black text-white">{formatPrice(tableCommands.reduce((sum, command) => sum + command.total, 0))}</p>
            <p className="mt-1 text-sm text-muted-foreground">total somado das comandas abertas</p>
          </div>
          <div className={`rounded-2xl border p-4 ${printablePendingOrdersCount > 0 ? 'border-amber-500/30 bg-amber-500/10' : 'border-white/8 bg-[#151515]'}`}>
            <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${printablePendingOrdersCount > 0 ? 'text-amber-200' : 'text-white/60'}`}>Impressao</p>
            <p className="mt-2 text-2xl font-black text-white">{printablePendingOrdersCount}</p>
            <p className={`mt-1 text-sm ${printablePendingOrdersCount > 0 ? 'text-amber-100/80' : 'text-muted-foreground'}`}>
              {printablePendingOrdersCount > 0 ? 'pedidos podem ficar sem impressao automatica' : 'sem pendencias operacionais agora'}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 bg-[#151515] p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={orderSearch}
                onChange={(event) => setOrderSearch(event.target.value)}
                className="input pl-10"
                placeholder="Buscar por cliente, mesa, item ou codigo do pedido"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {(['all', 'table', 'delivery', 'counter'] as const).map((filter) => {
                const isActive = orderTypeFilter === filter;
                const label = filter === 'all' ? 'Todos' : orderTypeLabel[filter];

                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setOrderTypeFilter(filter)}
                    className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'border-primary/50 bg-primary/15 text-primary'
                        : 'border-[#2A2A2A] text-muted-foreground hover:border-primary/30 hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { id: 'operation' as const, label: 'Operacao', description: 'Fila de preparo, checkout e entregas' },
            { id: 'commands' as const, label: 'Comandas', description: 'Consumo por mesa e fechamento' },
          ].map((view) => {
            const isActive = boardView === view.id;

            return (
              <button
                key={view.id}
                type="button"
                onClick={() => setBoardView(view.id)}
                className={`rounded-2xl border px-4 py-3 text-left transition ${
                  isActive
                    ? 'border-primary/50 bg-primary/12 text-white'
                    : 'border-[#2A2A2A] bg-[#111] text-muted-foreground hover:border-primary/25 hover:text-white'
                }`}
              >
                <div className="text-sm font-semibold">{view.label}</div>
                <div className="mt-1 text-xs text-muted-foreground">{view.description}</div>
              </button>
            );
          })}
        </div>

        {printFeedback && (
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] px-4 py-3 text-sm text-white/80">
            {printFeedback}
          </div>
        )}

        {!printingModuleEnabled && (
          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            O modulo de impressao esta pausado nas configuracoes do restaurante.
          </div>
        )}

        {printingModuleEnabled && activePrintConfigs.length === 0 && (
          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            Nenhuma estacao ativa encontrada. Cadastre uma estacao em Impressao para habilitar o fluxo.
          </div>
        )}

        {currentPlatform === 'DESKTOP' && printingModuleEnabled && (
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] px-4 py-3 text-sm text-muted-foreground">
            O painel detectou desktop. Teste final deve ser feito no celular/tablet real do restaurante para validar deep link/share.
          </div>
        )}

        {boardView === 'operation' && awaitingOnlinePaymentOrders.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2.5 px-1">
              <CreditCard className="h-4 w-4 text-yellow-300" />
              <span className="text-sm font-semibold text-white">Delivery aguardando pagamento online</span>
            </div>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              {sortedAwaitingOnlinePaymentOrders.map((order) => (
                <AwaitingOnlinePaymentCard key={order.id} order={order} onCancel={cancel} cancelling={updatingOrderActionKey === `${order.id}:cancel`} />
              ))}
            </div>
          </div>
        )}

        {boardView === 'commands' && filteredTableCommands.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2.5 px-1">
              <Package className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-white">Comandas abertas por mesa</span>
              <span className="ml-auto rounded-full bg-[#1A1A1A] px-2 py-0.5 text-xs font-bold text-muted-foreground">
                {filteredTableCommands.length}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              {filteredTableCommands.map((command) => (
                <div key={command.id} className="card space-y-4 border-primary/15 bg-primary/[0.03] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/80">
                        Mesa {command.tableNumber}
                      </p>
                      <p className="mt-2 text-lg font-bold text-white">
                        {command.ordersCount} {command.ordersCount === 1 ? 'pedido aberto' : 'pedidos abertos'}
                      </p>
                      {command.customerNames.length > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {command.customerNames.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 text-right">
                      <span className="inline-flex rounded-full bg-white/[0.05] px-3 py-1 text-xs font-semibold text-white/75">
                        {timeAgo(command.latestAt)}
                      </span>
                      <div className="flex flex-wrap justify-end gap-2">
                        <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-semibold text-primary">
                          Em aberto
                        </span>
                        {activePrintConfigs.length === 0 && printingModuleEnabled && (
                          <span className="rounded-full bg-amber-500/12 px-2.5 py-1 text-[11px] font-semibold text-amber-200">
                            Impressao pendente
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 rounded-2xl border border-white/8 bg-[#111] p-3">
                    {command.items.map((item) => (
                      <div key={item.key} className="flex items-start justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <p className="font-semibold text-white">
                            {item.quantity}x {item.name}
                          </p>
                          {item.notes.length > 0 && (
                            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                              {item.notes.join(' | ')}
                            </p>
                          )}
                        </div>
                        <span className="font-semibold text-white">{formatPrice(item.subtotal)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between border-t border-white/8 pt-2">
                    <span className="text-sm text-muted-foreground">Total da comanda</span>
                    <span className="text-lg font-black text-primary">{formatPrice(command.total)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => focusTableOrders(command.tableNumber)}
                      className="rounded-xl border border-[#2A2A2A] px-3 py-2.5 text-sm font-semibold text-white transition hover:border-primary/30 hover:text-primary"
                    >
                      Ver pedidos da mesa
                    </button>
                    <button
                      type="button"
                      onClick={() => requestCloseCommand(command)}
                      disabled={closingCommandTable === command.tableNumber}
                      className="btn-primary py-2.5 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {closingCommandTable === command.tableNumber ? 'Fechando...' : 'Fechar comanda'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loadError && !isLoading && (
          <div className="card p-5">
            <h2 className="text-lg font-semibold text-white">Pedidos indisponiveis</h2>
            <p className="mt-2 text-sm text-muted-foreground">{loadError}</p>
          </div>
        )}

        {boardView === 'commands' && filteredTableCommands.length === 0 && (
          <div className="card border-dashed p-8 text-center text-sm text-muted-foreground">
            {orderSearch || orderTypeFilter !== 'all'
              ? 'Nenhuma comanda encontrada com esse filtro.'
              : 'Nenhuma comanda aberta agora.'}
          </div>
        )}

        {boardView === 'operation' && (
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {STATUS_COLUMNS.map((column) => {
              const columnOrders = sortOrdersForDisplay(
                filteredOrders.filter((order) => order.status === column.status),
                column.status,
              );
              const Icon = column.icon;

              return (
                <div key={column.status} className="space-y-3">
                  <div className="flex items-center gap-2.5 px-1">
                    <Icon className={`h-4 w-4 ${column.color}`} />
                    <span className="text-sm font-semibold text-white">{column.label}</span>
                    <span className="ml-auto rounded-full bg-[#1A1A1A] px-2 py-0.5 text-xs font-bold text-muted-foreground">
                      {columnOrders.length}
                    </span>
                  </div>
                  <div className="min-h-[200px] space-y-3">
                    {isLoading ? (
                      <div className="card border-dashed p-6 text-center text-sm text-muted-foreground">
                        Carregando pedidos...
                      </div>
                    ) : columnOrders.length === 0 ? (
                      <div className="card border-dashed p-6 text-center text-sm text-muted-foreground">
                        {orderSearch || orderTypeFilter !== 'all' ? 'Nenhum pedido encontrado com esse filtro' : 'Nenhum pedido'}
                      </div>
                    ) : (
                      columnOrders.map((order, index) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          onAdvance={advance}
                          onCancel={cancel}
                          onPrint={(targetOrder) => openPrintModal(targetOrder, 'manual')}
                          queuePosition={index}
                          showPrintButton={printingModuleEnabled && activePrintConfigs.length > 0}
                          printJob={latestPrintJobByOrder.get(order.id)}
                          actionLoading={
                            updatingOrderActionKey === `${order.id}:advance`
                              ? 'advance'
                              : updatingOrderActionKey === `${order.id}:cancel`
                                ? 'cancel'
                                : null
                          }
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        isOpen={!!selectedOrder}
        onClose={closePrintModal}
        title={printModalMode === 'prompt' ? 'Novo pedido pronto para impressao' : 'Imprimir pedido'}
        description={
          printModalMode === 'prompt'
            ? 'Providers mobile seguem fluxo assistido. O bridge Android enfileira o job para auto print real fora do navegador.'
            : 'Escolha a estacao ativa e confirme o lancamento no dispositivo.'
        }
        footer={(
          <>
            <button onClick={closePrintModal} className="rounded-xl border border-[#2A2A2A] px-4 py-3 text-sm font-semibold text-white">
              Agora nao
            </button>
            <button
              onClick={() => void printSelectedOrder()}
              disabled={!selectedPrinterConfigId || isPrinting}
              className="btn-primary"
            >
              {isPrinting ? 'Imprimindo...' : 'Imprimir pedido'}
            </button>
          </>
        )}
      >
        {selectedOrder && (
          <div className="space-y-5">
            <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
              <p className="text-sm font-semibold text-white">Pedido {selectedOrder.id.slice(-6).toUpperCase()}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedOrder.customerName} · {selectedOrder.type === 'delivery' ? 'Delivery' : selectedOrder.type === 'table' ? `Mesa ${selectedOrder.tableNumber}` : 'Retirada'}
              </p>
              <p className="mt-2 text-sm text-white">{formatPrice(selectedOrder.total)}</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-white">Estacao de impressao</label>
              <select
                value={selectedPrinterConfigId}
                onChange={(event) => setSelectedPrinterConfigId(event.target.value)}
                className="input"
              >
                <option value="">Selecione uma estacao</option>
                {activePrintConfigs.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.name} · {config.providerMetadata.label}
                  </option>
                ))}
              </select>
            </div>

            {selectedPrinterConfigId && (
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4 text-sm text-muted-foreground">
                {activePrintConfigs.find((config) => config.id === selectedPrinterConfigId)?.provider === 'GARCOM_DIGITAL_BRIDGE' ? (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-300" />
                    <span>Esta estacao enfileira o pedido para o bridge Android autenticado. O navegador nao envia bytes para a impressora.</span>
                  </div>
                ) : activePrintConfigs.find((config) => config.id === selectedPrinterConfigId)?.providerMetadata.requiresExternalApp ? (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-300" />
                    <span>Esta estacao depende de app externo do fabricante. Valide no aparelho real do restaurante.</span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <Printer className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Esta estacao usa fallback assistido por compartilhamento/impressao do dispositivo.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!selectedCommand}
        onClose={() => setSelectedCommand(null)}
        title={selectedCommand ? `Fechar comanda da mesa ${selectedCommand.tableNumber}` : 'Fechar comanda'}
        description="Revise o total e os pedidos vinculados antes de encerrar a comanda."
        footer={(
          <>
            <button
              type="button"
              onClick={() => setSelectedCommand(null)}
              className="rounded-xl border border-[#2A2A2A] px-4 py-3 text-sm font-semibold text-white"
            >
              Voltar
            </button>
              <button
                type="button"
                onClick={() => {
                  if (!selectedCommand) return;
                  void closeTableCommand(selectedCommand);
                  setSelectedCommand(null);
                }}
                disabled={
                  !selectedCommand
                  || closingCommandTable === selectedCommand.tableNumber
                  || (closeCommandMarkAsPaid && !closeCommandPaymentMethod)
                }
                className="btn-primary"
              >
                {selectedCommand && closingCommandTable === selectedCommand.tableNumber ? 'Fechando comanda...' : 'Confirmar fechamento'}
            </button>
          </>
        )}
      >
        {selectedCommand && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/8 bg-[#151515] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/60">Mesa</p>
                <p className="mt-2 text-xl font-black text-white">{selectedCommand.tableNumber}</p>
              </div>
              <div className="rounded-2xl border border-white/8 bg-[#151515] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/60">Pedidos</p>
                <p className="mt-2 text-xl font-black text-white">{selectedCommand.ordersCount}</p>
              </div>
              <div className="rounded-2xl border border-primary/20 bg-primary/[0.05] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">Total final</p>
                <p className="mt-2 text-xl font-black text-primary">{formatPrice(selectedCommand.total)}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-white/8 bg-[#151515] p-4">
              <p className="text-sm font-semibold text-white">Resumo do consumo</p>
              <div className="mt-3 space-y-2">
                {selectedCommand.items.map((item) => (
                  <div key={item.key} className="flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-semibold text-white">{item.quantity}x {item.name}</p>
                      {item.notes.length > 0 && (
                        <p className="mt-1 text-[11px] text-muted-foreground">{item.notes.join(' | ')}</p>
                      )}
                    </div>
                    <span className="text-white">{formatPrice(item.subtotal)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/8 bg-[#151515] p-4 space-y-4">
              <label className="flex items-center gap-3 text-sm text-white">
                <input
                  type="checkbox"
                  checked={closeCommandMarkAsPaid}
                  onChange={(event) => setCloseCommandMarkAsPaid(event.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                Marcar esta comanda como paga ao fechar
              </label>

              {closeCommandMarkAsPaid && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.24em] text-white/60">
                    Forma de pagamento recebida
                  </label>
                  <select
                    value={closeCommandPaymentMethod}
                    onChange={(event) => setCloseCommandPaymentMethod(event.target.value as PaymentMethod)}
                    className="input appearance-none bg-[#101010]"
                  >
                    <option value="cash">Dinheiro</option>
                    <option value="card_on_delivery">Cartao</option>
                    <option value="mercado_pago">Mercado Pago</option>
                  </select>
                </div>
              )}

              {!closeCommandMarkAsPaid && (
                <p className="text-xs text-amber-300">
                  A comanda sera encerrada, mas os pedidos nao serao baixados como pagos automaticamente.
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AdminOrders;
